import type { FC, ReactNode } from 'react';
import type { GridSize } from '@mui/material';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type {
  DashboardWidgetConfig,
  DashboardWidgetSize,
} from 'src/types/dashboard';
import { CodingResultsContextProvider } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import CrossSurveyBenchmarkProvider from 'src/components/dashboard/DashboardContext/CrossSurveyBenchmarkContext';
import StatisticsContextProvider from 'src/components/dashboard/DashboardContext/StatisticsContext';
import { TagCloudContextProvider } from 'src/components/dashboard/DashboardContext/TagCloudContext';
import WidgetGridWrapper from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/components/widgetGridWrapper';
import WidgetGroupGridWrapper from 'src/components/dashboard/DashboardPage/components/WidgetWrapper/components/widgetGroupGridWrapper';
import AverageResolutionTimeWidget from 'src/components/dashboard/DashboardPage/widgets/AverageResolutionTimeWidget/AverageResolutionTimeWidget';
import BarDiagramWidget from 'src/components/dashboard/DashboardPage/widgets/BarDiagramWidget';
import BarTableWidget from 'src/components/dashboard/DashboardPage/widgets/BarTableWidget';
import BubbleWidget from 'src/components/dashboard/DashboardPage/widgets/BubbleWidget';
import ComparisonOfDemographics from 'src/components/dashboard/DashboardPage/widgets/ComparisonOfDemographicsWidget';
import ComparisonOfUnitsWidget from 'src/components/dashboard/DashboardPage/widgets/ComparisonOfUnitsWidget/ComparisonOfUnitsWidget';
import DeepDiveQuestionsWidget from 'src/components/dashboard/DashboardPage/widgets/DeepDiveQuestionsWidget/DeepDiveQuestionsWidget';
import DetailedOrgUnitResponseRateWidget from 'src/components/dashboard/DashboardPage/widgets/DetailedOrgUnitResponseRateWidget/DetailedOrgUnitResponseRateWidget';
import DetractorTicketsWidget from 'src/components/dashboard/DashboardPage/widgets/DetractorTicketsWidget/DetractorTicketsWidget';
import DoughnutGroupWidget from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/DoughnutGroupWidget';
import FlexibleChartJsWidget from 'src/components/dashboard/DashboardPage/widgets/FlexibleChartWidget';
import GroupedBarWidget from 'src/components/dashboard/DashboardPage/widgets/GroupedBarWidget';
import HorizontalBarChartWidget from 'src/components/dashboard/DashboardPage/widgets/HorizontalBarChartWidget';
import InsightWheelWidget from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget';
import MyFollowUpStateWidget from 'src/components/dashboard/DashboardPage/widgets/MyFollowUpStateWidget/MyFollowUpStateWidget';
import NpsDistributionWidget from 'src/components/dashboard/DashboardPage/widgets/NpsDistributionWidget/NpsDistributionWidget';
import NpsScoreGaugeWidget from 'src/components/dashboard/DashboardPage/widgets/NpsGaugeWidget/NpsScoreGaugeWidget';
import NpsTrendAnalysisWidget from 'src/components/dashboard/DashboardPage/widgets/NpsTrendAnalysisWidget';
import OpenCommentsWidget from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget';
import HRQSOpenCommentsWidget from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/HRQSOpenCommentsWidget/HRQSOpenCommentsWidget';
import OpenCommentsLegend from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/Legend';
import OrgUnitResponseRateWidget from 'src/components/dashboard/DashboardPage/widgets/OrgUnitResponseRateWidget/OrgUnitResponseRateWidget';
import QuestionWidget from 'src/components/dashboard/DashboardPage/widgets/QuestionWidget/QuestionWidget';
import ReasonsForLeavingWidget from 'src/components/dashboard/DashboardPage/widgets/ReasonsForLeavingWidget/ReasonsForLeavingWidget';
import ResponseFunnelWidget from 'src/components/dashboard/DashboardPage/widgets/ResponseFunnelWidget/ResponseFunnelWidget';
import ResponseManager from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget';
import ResponseRateWidgetLegacy from 'src/components/dashboard/DashboardPage/widgets/ResponseRateWidget/legacy';
import ResponseRateWidget from 'src/components/dashboard/DashboardPage/widgets/ResponseRateWidget/ResponseRateWidget';
import RootCauseWidget from 'src/components/dashboard/DashboardPage/widgets/RootCauseWidget';
import SectionWidget from 'src/components/dashboard/DashboardPage/widgets/SectionWidget';
import SpeedometerReWidget from 'src/components/dashboard/DashboardPage/widgets/SpeedometerReWidget';
import SpeedometerWidget from 'src/components/dashboard/DashboardPage/widgets/SpeedometerWidget/SpeedometerWidget';
import StackedBarWidget from 'src/components/dashboard/DashboardPage/widgets/StackedBarWidget';
import StackedBarLegend from 'src/components/dashboard/DashboardPage/widgets/StackedBarWidget/Legend';
import TableWidget from 'src/components/dashboard/DashboardPage/widgets/TableWidget';
import TagCloudWidget from 'src/components/dashboard/DashboardPage/widgets/TagCloudWidget';
import TextWidget from 'src/components/dashboard/DashboardPage/widgets/TextWidget';
import TotalTicketsWidget from 'src/components/dashboard/DashboardPage/widgets/TotalTicketsWidget/TotalTicketsWidget';
import TrendAnalysisBenchmarkComparisonWidget from 'src/components/dashboard/DashboardPage/widgets/TrendAnalysisBenchmarkComparisonWidget';
import TrendAnalysisResponseRateWidget from 'src/components/dashboard/DashboardPage/widgets/TrendAnalysisResponseRateWidget';

export const getWidget = (
  widgetType: number,
): FC<{
  config: DashboardWidgetConfig;
  className?: string;
}> | null =>
  ({
    [DashboardWidgetType.RESPONSE_MANAGER]: ResponseManager,
    [DashboardWidgetType.DOUGHNUT]: DoughnutGroupWidget,
    [DashboardWidgetType.RESPONSE_RATE_LEGACY]: ResponseRateWidgetLegacy,
    [DashboardWidgetType.STACKED_BAR]: StackedBarWidget,
    [DashboardWidgetType.TABLE]: TableWidget,
    [DashboardWidgetType.BUBBLE]: BubbleWidget,
    [DashboardWidgetType.TEXT]: TextWidget,
    [DashboardWidgetType.SECTION]: SectionWidget,
    [DashboardWidgetType.BAR_TABLE]: BarTableWidget,
    [DashboardWidgetType.OPEN_COMMENTS]: OpenCommentsWidget,
    [DashboardWidgetType.TAG_CLOUD]: TagCloudWidget,
    [DashboardWidgetType.INSIGHT_WHEEL]: InsightWheelWidget,
    [DashboardWidgetType.TREND_ANALYSIS]: GroupedBarWidget,
    [DashboardWidgetType.DETAILED_ORG_UNIT_RESPONSE_RATE]:
      DetailedOrgUnitResponseRateWidget,
    [DashboardWidgetType.ORG_UNIT_RESPONSE_RATE]: OrgUnitResponseRateWidget,
    [DashboardWidgetType.RESPONSE_FUNNEL]: ResponseFunnelWidget,
    [DashboardWidgetType.SPEEDOMETER]: SpeedometerWidget,
    [DashboardWidgetType.SPEEDOMETER_RE]: SpeedometerReWidget,
    [DashboardWidgetType.COMPARISON_OF_UNITS]: ComparisonOfUnitsWidget,
    [DashboardWidgetType.NPS_DISTRIBUTION]: NpsDistributionWidget,
    [DashboardWidgetType.NPS_SCORE_GAUGE]: NpsScoreGaugeWidget,
    [DashboardWidgetType.REASONS_FOR_LEAVING]: ReasonsForLeavingWidget,
    [DashboardWidgetType.RESPONSE_RATE]: ResponseRateWidget,
    [DashboardWidgetType.QUESTION]: QuestionWidget,
    [DashboardWidgetType.AVERAGE_RESOLUTION_TIME]: AverageResolutionTimeWidget,
    [DashboardWidgetType.DETRACTOR_TICKETS]: DetractorTicketsWidget,
    [DashboardWidgetType.ROOT_CAUSES]: RootCauseWidget,
    [DashboardWidgetType.TOTAL_TICKETS]: TotalTicketsWidget,
    [DashboardWidgetType.DEEP_DIVE_QUESTIONS]: DeepDiveQuestionsWidget,
    [DashboardWidgetType.GENERIC_TREND_ANALYSIS]:
      TrendAnalysisBenchmarkComparisonWidget,
    [DashboardWidgetType.OPEN_COMMENTS_WIDGET_ENDPOINT]: HRQSOpenCommentsWidget,
    [DashboardWidgetType.MYFOLLOWUP_STATE]: MyFollowUpStateWidget,
    [DashboardWidgetType.MYFOLLOWUP_SURVEY_OVERVIEW]: HorizontalBarChartWidget,
    [DashboardWidgetType.BAR_DIAGRAM]: BarDiagramWidget,
    [DashboardWidgetType.COMPARISON_OF_DEMOGRAPHICS]: ComparisonOfDemographics,
    [DashboardWidgetType.NPS_TREND_ANALYSIS]: NpsTrendAnalysisWidget,
    [DashboardWidgetType.FLEXIBLE_CHART]: FlexibleChartJsWidget,
    [DashboardWidgetType.TREND_ANALYSIS_RESPONSE_RATE]:
      TrendAnalysisResponseRateWidget,
  })[widgetType] || null;

/**
 * The widget size can be configured in the backend. It is set to 'auto' by default which means
 * that this map of widgets sizes will be used to determine the default size of the widget.
 * If no default size is configured here XL will be used instead.
 */
export const getWidgetDefaultSize = (widgetType: number): DashboardWidgetSize =>
  (({
    [DashboardWidgetType.DOUGHNUT]: 'S',
    [DashboardWidgetType.TABLE]: 'M',
    [DashboardWidgetType.DETAILED_ORG_UNIT_RESPONSE_RATE]: 'XL',
    [DashboardWidgetType.ORG_UNIT_RESPONSE_RATE]: 'L',
    [DashboardWidgetType.RESPONSE_FUNNEL]: 'XL',
    [DashboardWidgetType.SPEEDOMETER]: 'S',
    [DashboardWidgetType.COMPARISON_OF_UNITS]: 'XL',
    [DashboardWidgetType.NPS_DISTRIBUTION]: 'L',
    [DashboardWidgetType.NPS_SCORE_GAUGE]: 'S',
    [DashboardWidgetType.COMPARISON_OF_DEMOGRAPHICS]: 'XL',
    [DashboardWidgetType.REASONS_FOR_LEAVING]: 'XL',
    [DashboardWidgetType.RESPONSE_RATE]: 'XL',
    [DashboardWidgetType.QUESTION]: 'S',
    [DashboardWidgetType.AVERAGE_RESOLUTION_TIME]: 'XL',
    [DashboardWidgetType.DETRACTOR_TICKETS]: 'S',
    [DashboardWidgetType.ROOT_CAUSES]: 'XL',
    [DashboardWidgetType.TOTAL_TICKETS]: 'L',
    [DashboardWidgetType.DEEP_DIVE_QUESTIONS]: 'L',
    [DashboardWidgetType.SPEEDOMETER_RE]: 'S',
    [DashboardWidgetType.MYFOLLOWUP_STATE]: 'S',
    [DashboardWidgetType.MYFOLLOWUP_SURVEY_OVERVIEW]: 'L',
    [DashboardWidgetType.NPS_TREND_ANALYSIS]: 'XL',
    [DashboardWidgetType.FLEXIBLE_CHART]: 'L',
    [DashboardWidgetType.TREND_ANALYSIS_RESPONSE_RATE]: 'XL',
  })[widgetType] || 'XL') as DashboardWidgetSize;

/**
 * If a widget should have a legend add it here
 */
export const getWidgetLegend = (widgetId: number): FC | null =>
  ({
    [DashboardWidgetType.STACKED_BAR]: StackedBarLegend,
    [DashboardWidgetType.OPEN_COMMENTS]: OpenCommentsLegend,
  })[widgetId] || null;

/**
 * If a widget requires context providers add them here
 */
export const getWidgetContextProviders = (
  widgetId: number,
):
  | FC<{
      config?: DashboardWidgetConfig;
      children: ReactNode;
    }>[]
  | null =>
  ({
    [DashboardWidgetType.RESPONSE_MANAGER]: [StatisticsContextProvider],
    [DashboardWidgetType.DOUGHNUT]: [
      StatisticsContextProvider,
      CrossSurveyBenchmarkProvider,
    ],
    [DashboardWidgetType.RESPONSE_RATE_LEGACY]: [StatisticsContextProvider],
    [DashboardWidgetType.STACKED_BAR]: [StatisticsContextProvider],
    [DashboardWidgetType.TABLE]: [StatisticsContextProvider],
    [DashboardWidgetType.BUBBLE]: [StatisticsContextProvider],
    [DashboardWidgetType.BAR_TABLE]: [StatisticsContextProvider],
    [DashboardWidgetType.OPEN_COMMENTS]: [CodingResultsContextProvider],
    [DashboardWidgetType.OPEN_COMMENTS_WIDGET_ENDPOINT]: [
      CodingResultsContextProvider,
    ],
    [DashboardWidgetType.TAG_CLOUD]: [TagCloudContextProvider],
    [DashboardWidgetType.INSIGHT_WHEEL]: [CodingResultsContextProvider],
    [DashboardWidgetType.TREND_ANALYSIS]: [StatisticsContextProvider],
    [DashboardWidgetType.SPEEDOMETER_RE]: [
      StatisticsContextProvider,
      CrossSurveyBenchmarkProvider,
    ],
    [DashboardWidgetType.BAR_DIAGRAM]: [StatisticsContextProvider],
    [DashboardWidgetType.FLEXIBLE_CHART]: [StatisticsContextProvider],
  })[widgetId] || null;

/**
 * If a widget requires a custom grid wrapper, add it here
 */
export const getWidgetGridWrapper = (
  widgetId: number,
): FC<{
  config: DashboardWidgetConfig;
  gridSize: GridSize | 'grow';
  className: string;
  children: ReactNode;
}> =>
  ({
    [DashboardWidgetType.DOUGHNUT]: WidgetGroupGridWrapper,
    [DashboardWidgetType.SPEEDOMETER_RE]: WidgetGroupGridWrapper,
  })[widgetId] || WidgetGridWrapper;
